import React, {
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type Props = {
  value: string | number;
  onChange: (value: string | number) => void;
  label: string;
};

type OptionElementProps = {
  value?: string | number;
  children?: React.ReactNode;
};

type OptionMeta = {
  value: string | number;
  label: string;
};

const getTextContent = (node: React.ReactNode): string => {
  return React.Children.toArray(node)
    .map((child) =>
      typeof child === "string" || typeof child === "number"
        ? child.toString()
        : ""
    )
    .join("")
    .trim();
};

const isEqualValue = (a: string | number, b: string | number) => {
  return String(a) === String(b);
};

const SelectBox: React.FC<PropsWithChildren<Props>> = ({
  value,
  onChange,
  label,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const options = useMemo<OptionMeta[]>(() => {
    const opts: OptionMeta[] = [];
    React.Children.forEach(children, (child) => {
      if (!React.isValidElement<OptionElementProps>(child)) return;
      const labelText = getTextContent(child.props.children);
      const optValue = child.props.value ?? labelText;
      opts.push({
        value: optValue,
        label: labelText || String(optValue),
      });
    });
    return opts;
  }, [children]);

  const selectedIndex = useMemo(
    () => options.findIndex((opt) => isEqualValue(opt.value, value)),
    [options, value]
  );
  const selectedOption =
    selectedIndex >= 0 ? options[selectedIndex] : undefined;

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const fallbackIndex = selectedIndex >= 0 ? selectedIndex : 0;
    optionRefs.current[fallbackIndex]?.focus();
  }, [isOpen, selectedIndex]);

  const display = `${label}: ${selectedOption?.label ?? value}`;

  const handleSelect = (opt: OptionMeta) => {
    setIsOpen(false);
    if (!isEqualValue(opt.value, value)) {
      onChange(opt.value);
    }
  };

  const handleButtonKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setIsOpen(true);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen((prev) => !prev);
    }
  };

  const handleOptionKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const dir = event.key === "ArrowDown" ? 1 : -1;
      const nextIndex = (index + dir + options.length) % options.length;
      optionRefs.current[nextIndex]?.focus();
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const opt = options[index];
      if (opt) handleSelect(opt);
    }
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <button
        type="button"
        ref={buttonRef}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={handleButtonKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`relative flex h-[clamp(36px,4.5vw,54px)] w-full items-center justify-between rounded-[0.5vw] px-[3vw] text-[clamp(12px,1.6vw,16px)] font-semibold text-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60`}
      >
        <span className="block w-full truncate text-left" aria-live="polite">
          {display}
        </span>
        <span
          className={`ml-2 inline-flex h-5 w-5 items-center justify-center text-white transition-transform`}
        >
          <span className="block h-2 w-2 rotate-45 border-b-2 border-r-2 border-current" />
        </span>
      </button>

      {isOpen && options.length > 0 && (
        <div
          role="listbox"
          aria-label={label}
          className="absolute w-full left-0 right-0 top-[calc(100%+0.5vw)] z-20 overflow-hidden rounded-[0.5vw] border border-border bg-darkestBlue shadow-2xl shadow-black/40 backdrop-blur-xl"
        >
          <div className="max-h-[40vh] overflow-y-auto py-[0.4vw] rounded-[0.5vw]">
            {options.map((opt, index) => {
              const isSelected = isEqualValue(opt.value, value);
              return (
                <button
                  type="button"
                  key={`${opt.value}-${index}`}
                  ref={(node) => {
                    optionRefs.current[index] = node;
                  }}
                  role="option"
                  aria-selected={isSelected}
                  className={`flex w-full items-center justify-between px-[2vw] py-[0.8vw] text-left text-[clamp(12px,1.5vw,15px)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    isSelected
                      ? "bg-gray-700 text-primary"
                      : "text-white hover:bg-gray-800 hover:text-white"
                  }`}
                  onClick={() => handleSelect(opt)}
                  onKeyDown={(event) => handleOptionKeyDown(event, index)}
                >
                  <span className="flex-1 truncate">{opt.label}</span>
                  {isSelected && (
                    <span className="ml-3 inline-flex h-3 w-3 items-center justify-center rounded-full text-[10px] font-black uppercase tracking-wide text-primary">
                      ●
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectBox;
